import { assign, createMachine } from 'xstate';

const DEFAULT_MAX_RETRIES = 3;

export function createTokenLifecycleMachine({
  fetchAuthToken,
  tokenMsUntilExpiration,
  maxRetries = DEFAULT_MAX_RETRIES,
}) {
  return createMachine(
    {
      id: 'token-lifecycle',
      predictableActionArguments: true,
      preserveActionOrder: true,
      initial: 'FETCH_TOKEN',
      context: {
        attempts: 0,
        response: null,
        error: null,
      },
      states: {
        FETCH_TOKEN: {
          invoke: {
            id: 'fetch-auth-token',
            src: fetchAuthToken,
            onDone: {
              target: 'CHECK_RESPONSE',
              actions: assign({
                response: (_context, event) => event.data,
                error: () => null,
              }),
            },
            onError: {
              target: 'CHECK_ERROR',
              actions: assign({
                error: (_context, event) => event.data,
                response: () => null,
              }),
            },
          },
        },
        CHECK_RESPONSE: {
          always: [
            {
              target: 'UNAUTHORIZED',
              cond: 'isUnauthorizedResponse',
            },
            {
              target: 'NO_ACCESS_TOKEN',
              cond: 'hasNoAccessToken',
            },
            {
              target: 'RETRY',
              cond: 'isExpiredAndCanRetry',
            },
            {
              target: 'EXPIRED',
              cond: 'isExpiredToken',
            },
            {
              target: 'SUCCESS',
            },
          ],
        },
        CHECK_ERROR: {
          always: [
            {
              target: 'UNAUTHORIZED',
              cond: 'isUnauthorizedError',
            },
            {
              target: 'RETRY',
              cond: 'canRetry',
            },
            {
              target: 'ERROR',
            },
          ],
        },
        RETRY: {
          after: {
            RETRY_DELAY: {
              target: 'FETCH_TOKEN',
              actions: assign({
                attempts: (context) => context.attempts + 1,
              }),
            },
          },
        },
        SUCCESS: {
          type: 'final',
          data: (context) => ({
            status: 'success',
            attempts: context.attempts,
            data: context.response,
          }),
        },
        UNAUTHORIZED: {
          type: 'final',
          data: (context) => ({
            status: 'unauthorized',
            attempts: context.attempts,
            data: context.response,
            error: context.error,
          }),
        },
        NO_ACCESS_TOKEN: {
          type: 'final',
          data: (context) => ({
            status: 'no_access_token',
            attempts: context.attempts,
            data: context.response,
          }),
        },
        EXPIRED: {
          type: 'final',
          data: (context) => ({
            status: 'expired',
            attempts: context.attempts,
            data: context.response,
          }),
        },
        ERROR: {
          type: 'final',
          data: (context) => ({
            status: 'error',
            attempts: context.attempts,
            error: context.error,
          }),
        },
      },
    },
    {
      delays: {
        RETRY_DELAY: (context) => context.attempts * 1000,
      },
      guards: {
        isUnauthorizedResponse: (context) => (context.response?.error ?? null) === 'unauthorized',
        hasNoAccessToken: (context) => !context.response?.access_token,
        isExpiredToken: (context) => {
          if (!context.response?.access_token) {
            return false;
          }

          return tokenMsUntilExpiration(context.response.access_token, context.response) <= 0;
        },
        isExpiredAndCanRetry: (context) => {
          if (!context.response?.access_token) {
            return false;
          }

          return (
            context.attempts < maxRetries &&
            tokenMsUntilExpiration(context.response.access_token, context.response) <= 0
          );
        },
        isUnauthorizedError: (context) =>
          (context.error?.error ?? context.error?.data?.error ?? null) === 'unauthorized',
        canRetry: (context) => context.attempts < maxRetries,
      },
    },
  );
}
